import {keepPreviousData, useQuery} from '@tanstack/react-query'
import {useMemo, useState} from 'react'
import {useDebounceValue} from 'usehooks-ts'

import http from '../../../lib/http'
import * as Table from '../../claim-history/simple-table'
import {PageHeader} from '../components/page-header'
import {FriendlyDateTime} from "../../shared/Date/friendly-date-time";


export interface Log extends Table.Item {
  created_at: string;
  data: {
    module: string;
    component: string;
    message: string;
    cache?: Record<string, unknown>;
    input: {
      id: string;
      name: string;
      group: string;
      entityId?: string;
      claimNumber?: string;
      groupNumber?: string;
    };
    meta: {
      duration: number;
      startedAt?: string;
      finishedAt?: string;
    };
    response?: {
      status?: number;
      data?: any;
      headers?: Record<string, string>;
    };
    request?: {
      url?: string;
      data?: string;
    };
    success?: boolean;
  };
  id: number;
  updated_at: string;
}

interface ApiResponse {
    ok: boolean
    data: {
        logs: Log[]
        meta: {
            total: number
        }
    }
}

export function PortalIndex(): JSX.Element {
    const [page, setPage] = useState(0) // 0-based
    const [pageSize, setPageSize] = useState(10)

    // Filters
    const [statusFilter, setStatusFilter] = useState<string>('')
    const [relationshipFilter, setRelationshipFilter] = useState<string>('')
    const [memberId, setMemberId] = useState<string>('')
    const [lucentMemberId, setLucentMemberId] = useState<string>('')
    const [userEmail, setUserEmail] = useState<string>('')

    // Sorting
    const [sort, setSort] = useState<Table.Sort<Log>>({field: 'created_at', direction: 'desc'})

    // Debounced filter values to avoid excessive requests
    const [debouncedStatus] = useDebounceValue(statusFilter, 400)
    const [debouncedRelationship] = useDebounceValue(relationshipFilter, 400)
    const [debouncedMemberId] = useDebounceValue(memberId, 600)
    const [debouncedLucentMemberId] = useDebounceValue(lucentMemberId, 600)
    const [debouncedUserEmail] = useDebounceValue(userEmail, 600)

    const {data, status, error} = useQuery<ApiResponse>({
        queryKey: ['zelis-responses', {
            page,
            pageSize,
            statusFilter: debouncedStatus,
            relationshipFilter: debouncedRelationship,
            memberId: debouncedMemberId,
            lucentMemberId: debouncedLucentMemberId,
            userEmail: debouncedUserEmail,
            sort
        }],
        queryFn: async () => {
            const sp = new URLSearchParams([
                ['page', (page + 1).toString()],
                ['pageSize', pageSize.toString()],
            ] as any)

            if (debouncedStatus.trim()) sp.set('status', debouncedStatus.trim())
            if (debouncedRelationship.trim()) sp.set('relationship', debouncedRelationship.trim())
            if (debouncedMemberId.trim()) sp.set('memberId', debouncedMemberId.trim())
            if (debouncedLucentMemberId.trim()) sp.set('lucentMemberId', debouncedLucentMemberId.trim())
            if (debouncedUserEmail.trim()) sp.set('userEmail', debouncedUserEmail.trim())
            if (sort?.field) {
                sp.set('sortBy', sort.field as string)
                sp.set('sortDir', sort.direction)
            }

            const response = await http.getRequest<ApiResponse>(`/admin/zelis-responses?${sp.toString()}`)
            return response
        },
        placeholderData: keepPreviousData,
    })

    const extractClaimNumber = (row: Log): string => {
        if (row.data.input.claimNumber) return row.data.input.claimNumber
        if (row.data.request?.data) {
            const match = row.data.request.data.match(/<claimNumber>(.*?)<\/claimNumber>/)
            return match?.[1] ? match[1] : '-'
        }
        return '-'
    }

    const extractEntityId = (row: Log): string => {
        if (row.data.input.entityId) return row.data.input.entityId
        if (row.data.request?.data) {
            const match = row.data.request.data.match(/<entityId>(.*?)<\/entityId>/)
            return match?.[1] ? match[1] : '-'
        }
        return '-'
    }

    const columns = useMemo<Array<Table.Column<Log>>>(() => [
        {
            name: 'created_at',
            header: "Created At",
            isSortable: true,
            getValue: (row) => <FriendlyDateTime datetime={row.created_at}/>
        },
        {
            name: 'component',
            header: 'Component',
            isSortable: true,
            getValue: (row) => row.data.component
        },
        {
            name: 'member_id',
            header: 'Member Id',
            isSortable: true,
            getValue: (row) => row.data.input.id
        },
        {
            name: 'claim_number',
            header: 'Claim Number',
            isSortable: true,
            getValue: (row) => extractClaimNumber(row)
        },
        {
            name: 'request_details',
            header: 'Request Details',
            gridArea: '1.5fr',
            getValue: (row) => {
                const entityId = extractEntityId(row)
                const claimNumber = extractClaimNumber(row)
                return (
                    <div style={{fontSize: '0.9em'}}>
                        {entityId !== '-' && <div><strong>Entity ID:</strong> {entityId}</div>}
                        {claimNumber !== '-' && <div><strong>Claim:</strong> {claimNumber}</div>}
                        {row.data.input.group && <div><strong>Group:</strong> {row.data.input.group}</div>}
                    </div>
                )
            }
        },
        {
            name: 'response_info',
            header: 'Response',
            gridArea: '1.2fr',
            getValue: (row) => {
                const status = row.data.response?.status
                const date = row.data.response?.headers?.date
                const success = row.data.success
                const responseData = row.data.response?.data
                const hasResponse = status || date || success !== undefined
                
                if (!hasResponse) {
                    return <span style={{color: '#999', fontSize: '0.85em'}}>No response data</span>
                }
                
                const dataCount = Array.isArray(responseData) ? responseData.length : null
                
                return (
                    <div style={{fontSize: '0.9em'}}>
                        {status && (
                            <div>
                                <strong>Status:</strong>{' '}
                                <span style={{color: status === 200 ? '#22c55e' : '#ef4444'}}>
                                    {status}
                                </span>
                            </div>
                        )}
                        {success !== undefined && (
                            <div>
                                <strong>Success:</strong>{' '}
                                <span style={{color: success ? '#22c55e' : '#ef4444'}}>
                                    {success ? 'Yes' : 'No'}
                                </span>
                            </div>
                        )}
                        {dataCount !== null && (
                            <div>
                                <strong>Data:</strong> {dataCount} {dataCount === 1 ? 'item' : 'items'}
                            </div>
                        )}
                        {date && <div style={{fontSize: '0.85em', color: '#666'}}>{date}</div>}
                    </div>
                )
            }
        },
        {
            name: 'duration',
            header: 'Duration',
            isSortable: true,
            getValue: (row) => (
                <div title={row.data.cache ? 'Retrieved from cache' : 'Retrieved from Zelis'}>
                    {row.data.meta.duration.toFixed(1)} ms
                </div>
            )
        },
        {
            name: 'message',
            header: 'Message',
            gridArea: '2fr',
            isSortable: true,
            getValue: (row) => row.data.message
        }
    ], [])

    const pagination = useMemo(() => ({
        page,
        pageSize,
        totalFiltered: data?.data?.meta.total ?? 0,
        totalUnfiltered: data?.data?.meta.total ?? 0,
    }), [page, pageSize, data?.data?.meta.total])

    return (
        <div>
            <PageHeader>
                <h1>Zelis Responses</h1>
                {/*<SearchInput*/}
                {/*    value={query}*/}
                {/*    placeholder='Search by patient info'*/}
                {/*    onChange={event => {*/}
                {/*        setQuery(event.target.value)*/}
                {/*        setPage(0)*/}
                {/*    }}*/}
                {/*/>*/}
            </PageHeader>

            <Table.Table
                data={data?.data?.logs ?? []}
                queryStatus={status}
                error={error as Error | undefined}
                noDataPlaceholder='No patients to display'
                columns={columns}
                sort={sort}
                onSortChange={(field, direction) => setSort({field: field as keyof Log, direction})}
                pagination={pagination}
                onPageChange={(value: number) => setPage(value - 1)}
                onPageSizeChange={(value: number) => {
                    setPageSize(value);
                    setPage(0)
                }}
                filters={[
                    {
                        name: 'active', value: statusFilter ? [statusFilter] : [], options: [
                            {value: 'Active'},
                            {value: 'Inactive'},
                        ]
                    },
                    {
                        name: 'relationship', value: relationshipFilter ? [relationshipFilter] : [], options: [
                            {value: 'Enrollee'},
                            {value: 'Dependent'},
                        ]
                    },
                    {
                        name: 'member_id',
                        value: memberId ? [memberId] : [],
                        render: ({onChange, value}) => (
                            <div style={{padding: '0.5rem'}}>
                                <input
                                    autoFocus
                                    type='text'
                                    defaultValue={(value?.[0] as string) ?? ''}
                                    placeholder='Filter by Alt Member ID'
                                    onChange={(e) => onChange(e.target.value ? [e.target.value] : [])}
                                    style={{width: 260, padding: '0.5rem', borderRadius: 8, border: '1px solid #ddd'}}
                                />
                            </div>
                        ),
                        isActive: !!memberId
                    },
                    {
                        name: 'lucent_member_id',
                        value: lucentMemberId ? [lucentMemberId] : [],
                        render: ({onChange, value}) => (
                            <div style={{padding: '0.5rem'}}>
                                <input
                                    autoFocus
                                    type='text'
                                    defaultValue={(value?.[0] as string) ?? ''}
                                    placeholder='Filter by Member ID'
                                    onChange={(e) => onChange(e.target.value ? [e.target.value] : [])}
                                    style={{width: 260, padding: '0.5rem', borderRadius: 8, border: '1px solid #ddd'}}
                                />
                            </div>
                        ),
                        isActive: !!lucentMemberId
                    },
                    {
                        name: 'matched_users', value: userEmail ? [userEmail] : [], render: ({onChange, value}) => (
                            <div style={{padding: '0.5rem'}}>
                                <input
                                    autoFocus
                                    type='text'
                                    defaultValue={(value?.[0] as string) ?? ''}
                                    placeholder='Filter by user email'
                                    onChange={(e) => onChange(e.target.value ? [e.target.value] : [])}
                                    style={{width: 260, padding: '0.5rem', borderRadius: 8, border: '1px solid #ddd'}}
                                />
                            </div>
                        ), isActive: !!userEmail
                    },
                ]}
                onFilterChange={(field, values) => {
                    const first = (values?.[0] as string) ?? ''
                    if (field === 'active') setStatusFilter(first)
                    if (field === 'relationship') setRelationshipFilter(first)
                    if (field === 'member_id') setMemberId(first)
                    if (field === 'lucent_member_id') setLucentMemberId(first)
                    if (field === 'matched_users') setUserEmail(first)
                    setPage(0)
                }}
                onFiltersClear={() => {
                    setStatusFilter('')
                    setRelationshipFilter('')
                    setMemberId('')
                    setLucentMemberId('')
                    setUserEmail('')
                    setPage(0)
                }}
            />
        </div>
    )
}

export default PortalIndex
